/**
 * PinKeypad — Reusable 6-digit PIN entry component
 *
 * Used by both WalletPage (setup + unlock gate) and TournamentDetail (unlock modal).
 * Features a visual dot display, on-screen numpad, error/loading states, and
 * shake animation on wrong PIN.
 */

import { useState, useCallback, useEffect } from 'react';
import { Loader, Delete } from 'lucide-react';

interface PinKeypadProps {
    /** Called with the 6-digit PIN when the user submits */
    onSubmit: (pin: string) => Promise<void>;
    /** Error message to display (set externally by the parent) */
    error?: string;
    /** Clear error callback */
    onClearError?: () => void;
    /** Label for the submit button */
    submitLabel?: string;
    /** Whether this is in a compact modal */
    compact?: boolean;
}

export default function PinKeypad({ onSubmit, error, onClearError, submitLabel = 'Confirm', compact }: PinKeypadProps) {
    const [digits, setDigits] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [shakeError, setShakeError] = useState(false);

    // Shake dots on error change
    useEffect(() => {
        if (error) {
            setShakeError(true);
            const t = setTimeout(() => setShakeError(false), 500);
            return () => clearTimeout(t);
        }
    }, [error]);

    const handleDigit = useCallback((d: string) => {
        if (loading) return;
        if (onClearError) onClearError();
        setDigits(prev => prev.length < 6 ? prev + d : prev);
    }, [loading, onClearError]);

    const handleDelete = useCallback(() => {
        if (loading) return;
        if (onClearError) onClearError();
        setDigits(prev => prev.slice(0, -1));
    }, [loading, onClearError]);

    const handleSubmit = useCallback(async () => {
        if (digits.length !== 6 || loading) return;
        setLoading(true);
        try {
            await onSubmit(digits);
        } catch {
            // Error is handled by the parent via the error prop
        } finally {
            setLoading(false);
            setDigits('');
        }
    }, [digits, loading, onSubmit]);

    // Keyboard support
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (loading) return;
            if (e.key >= '0' && e.key <= '9') {
                handleDigit(e.key);
            } else if (e.key === 'Backspace') {
                handleDelete();
            } else if (e.key === 'Enter' && digits.length === 6) {
                handleSubmit();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [loading, digits, handleDigit, handleDelete, handleSubmit]);

    return (
        <div className={compact ? '' : 'animate-in'}>
            {/* Dots Display */}
            <div className="pin-dots">
                {Array.from({ length: 6 }).map((_, i) => (
                    <div
                        key={i}
                        className={`pin-dot${digits[i] ? ' filled' : ''}${i === digits.length ? ' active' : ''}${shakeError ? ' error' : ''}`}
                    >
                        {digits[i] ? '●' : ''}
                    </div>
                ))}
            </div>

            {/* Error Message */}
            {error && <div className="pin-error">{error}</div>}

            {/* Loading State */}
            {loading ? (
                <div className="pin-loading">
                    <Loader size={28} className="spin" style={{ color: 'var(--accent)' }} />
                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Verifying…</span>
                </div>
            ) : (
                <>
                    {/* Numpad */}
                    <div className="pin-numpad">
                        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(d => (
                            <button key={d} className="pin-key" onClick={() => handleDigit(d)} type="button">
                                {d}
                            </button>
                        ))}
                        <button className="pin-key action" onClick={handleDelete} type="button">
                            <Delete size={20} />
                        </button>
                        <button className="pin-key" onClick={() => handleDigit('0')} type="button">
                            0
                        </button>
                        <button
                            className="pin-key submit"
                            onClick={handleSubmit}
                            disabled={digits.length !== 6}
                            type="button"
                        >
                            {submitLabel}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}
